export interface MetaPixelConfig {
  enabled: boolean
  pixelIds: string[]
  conversionsApiEnabled?: boolean
}

declare global {
  interface Window {
    fbq?: FbqFunction
    _fbq?: FbqFunction
  }
}

type FbqFunction = {
  (...args: unknown[]): void
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[]
  push?: FbqFunction
  loaded?: boolean
  version?: string
}

const PIXEL_ID_PATTERN = /^\d+$/

export function normalizePixelIds(ids?: string[]): string[] {
  if (!Array.isArray(ids)) return []
  const seen = new Set<string>()
  const result: string[] = []
  for (const raw of ids) {
    const id = String(raw ?? '').trim()
    if (id && PIXEL_ID_PATTERN.test(id) && !seen.has(id)) {
      seen.add(id)
      result.push(id)
    }
  }
  return result
}

export function parsePixelIdsText(text: string): string[] {
  return normalizePixelIds(text.split(/[\n,]+/))
}

export function formatPixelIdsText(ids: string[]): string {
  return normalizePixelIds(ids).join('\n')
}

export function parseMetaPixelConfig(raw?: string | null): MetaPixelConfig {
  if (!raw?.trim()) {
    return { enabled: false, pixelIds: [] }
  }

  try {
    const parsed = JSON.parse(raw) as Partial<MetaPixelConfig>
    return {
      enabled: parsed.enabled === true,
      pixelIds: normalizePixelIds(parsed.pixelIds),
      conversionsApiEnabled: parsed.conversionsApiEnabled === true,
    }
  } catch {
    return { enabled: false, pixelIds: [] }
  }
}

let initialized = false
let blocked = true

export function isMetaPixelBlocked(): boolean {
  return blocked
}

export function setMetaPixelBlocked(value: boolean): void {
  blocked = value
  if (blocked && typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('consent', 'revoke')
  } else if (!blocked && initialized && typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('consent', 'grant')
  }
}

export function initMetaPixel(pixelIds: string[]): boolean {
  if (!import.meta.client || blocked) return false

  const ids = normalizePixelIds(pixelIds)
  if (!ids.length) return false

  if (!window.fbq) {
    const fbq = function fbq(...args: unknown[]) {
      const fn = fbq as FbqFunction
      if (fn.callMethod) {
        fn.callMethod(...args)
      } else {
        fn.queue?.push(args)
      }
    } as FbqFunction
    fbq.queue = []
    window.fbq = fbq
    if (!window._fbq) window._fbq = fbq

    const script = document.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    const firstScript = document.getElementsByTagName('script')[0]
    firstScript?.parentNode?.insertBefore(script, firstScript)
  }

  for (const id of ids) {
    window.fbq?.('init', id)
  }

  window.fbq?.('consent', 'grant')
  initialized = true
  return true
}

export function disableMetaPixel(): void {
  setMetaPixelBlocked(true)
}

export function enableMetaPixel(): void {
  setMetaPixelBlocked(false)
}

export function trackMetaPageView(): void {
  if (!import.meta.client || blocked || !initialized || typeof window.fbq !== 'function') return
  window.fbq('track', 'PageView')
}

export function trackMetaEvent(
  event: string,
  params?: Record<string, unknown>,
  options?: { eventID?: string },
): void {
  if (!import.meta.client || blocked || !initialized || typeof window.fbq !== 'function') return
  if (options?.eventID) {
    window.fbq('track', event, params ?? {}, { eventID: options.eventID })
    return
  }
  window.fbq('track', event, params ?? {})
}

export function isMetaPixelInitialized(): boolean {
  return initialized
}
